import React from "react";
import { useNavigate } from "react-router-dom";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { CardSkeleton } from "../../../components/skeleton/ListSkeleton";
import { BookMyDentistListed, ReviewKey, getBookMyDentist } from "../analyticsApi";
import {
  checklistTone,
  formatNumber,
  formatPct,
  formatRs,
  formatStars,
  longDate,
  pctChange,
  ratingPct,
  reviewLabel,
  seriesIsEmpty,
} from "../analyticsUtils";
import { useAnalyticsPeriod } from "../useAnalyticsPeriod";
import { isBillingEnabled } from "../../../utils/clinicFeatures";
import { useAnalyticsFetch } from "../useAnalyticsFetch";
import PeriodPicker from "../components/PeriodPicker";
import KpiCard from "../components/KpiCard";
import Funnel from "../components/Funnel";
import EmptyState from "../components/EmptyState";
import { PeriodBarChart, PeriodLineChart } from "../components/charts";
import { CARD_PAD, Caption, ChartCard, LINK_BTN, SectionTitle, seriesColors, useIsDark } from "../components/ui";

const REVIEW_KEYS: ReviewKey[] = ["quality", "service", "comfort", "cleanliness", "communication"];

const BAND_TEXT = {
  green: "text-emerald-600 dark:text-emerald-400",
  amber: "text-amber-600 dark:text-amber-400",
  red: "text-rose-600 dark:text-rose-400",
};

const BAND_BAR = { green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-rose-500" };

function ReviewsCard({ reviews }: { reviews: BookMyDentistListed["reviews"] }) {
  const isDark = useIsDark();
  const color = seriesColors(isDark)[0];
  const series = reviews.series.map((p) => ({ bucket: p.month, overall: p.overall }));
  return (
    <div className={CARD_PAD}>
      <SectionTitle hint="All reviews received on BookMyDentist">Reviews</SectionTitle>
      {reviews.count === 0 ? (
        <p className="text-sm text-slate-400 dark:text-slate-500">No reviews yet</p>
      ) : (
        <>
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-bold tabular-nums text-amber-500">{formatStars(reviews.overall)}</span>
            <span className="text-xs text-slate-400 dark:text-slate-500">
              from {formatNumber(reviews.count)} {reviews.count === 1 ? "review" : "reviews"}
            </span>
          </div>
          <ul className="mt-4 space-y-2">
            {REVIEW_KEYS.map((k) => {
              const v = reviews.sub[k];
              return (
                <li key={k} className="flex items-center gap-3 text-xs">
                  <span className="w-28 shrink-0 text-slate-500 dark:text-slate-400">{reviewLabel(k)}</span>
                  <div className="flex-1 h-2 rounded-full bg-slate-100 dark:bg-slate-700/60 overflow-hidden" title={`${reviewLabel(k)}: ${formatStars(v)}`}>
                    <div className="h-full rounded-full" style={{ width: `${ratingPct(v)}%`, backgroundColor: color }} />
                  </div>
                  <span className="w-12 text-right tabular-nums font-medium text-slate-700 dark:text-slate-200">{formatStars(v)}</span>
                </li>
              );
            })}
          </ul>
          <div className="mt-4">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Overall rating, last 12 months</p>
            {seriesIsEmpty(series, ["overall"]) ? (
              <p className="text-xs text-slate-400 dark:text-slate-500">No reviews in the last 12 months</p>
            ) : (
              <PeriodLineChart series={series} specs={[{ key: "overall", label: "Overall" }]} height={200} />
            )}
          </div>
          <div className="mt-4">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Latest reviews</p>
            <ul className="divide-y divide-slate-100 dark:divide-slate-700">
              {reviews.latest.map((r, i) => (
                <li key={`${r.createdAt}-${i}`} className="py-2">
                  <div className="flex items-center justify-between gap-3 text-xs">
                    <span className="font-medium text-amber-500 tabular-nums">{formatStars(r.overall)}</span>
                    <span className="text-slate-400 dark:text-slate-500">{longDate(String(r.createdAt).slice(0, 10))}</span>
                  </div>
                  {r.comments ? (
                    <p className="text-sm text-slate-700 dark:text-slate-200 mt-0.5 whitespace-pre-line">{r.comments}</p>
                  ) : (
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 italic">No comment</p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

function ProfileCard({ data, onEdit }: { data: BookMyDentistListed; onEdit: () => void }) {
  const profile = data.profile;
  const band = checklistTone(profile?.percent);
  return (
    <div className={CARD_PAD}>
      <SectionTitle
        hint="What patients see on your public page"
        action={
          <button type="button" className={LINK_BTN} onClick={onEdit}>
            Edit profile
          </button>
        }
      >
        Profile completeness
      </SectionTitle>
      {data.status !== "published" && (
        <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300">
          Profile is a draft — not visible to the public
        </p>
      )}
      {!profile ? (
        <p className="text-sm text-slate-400 dark:text-slate-500">Not available</p>
      ) : (
        <>
          <div className="flex items-baseline gap-2">
            <span className={`text-4xl font-bold tabular-nums ${band ? BAND_TEXT[band] : "text-slate-800 dark:text-slate-100"}`}>{formatPct(profile.percent, 0)}</span>
            <span className="text-xs text-slate-400 dark:text-slate-500">
              {profile.done} of {profile.total} done
            </span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-slate-100 dark:bg-slate-700/60 overflow-hidden">
            <div className={`h-full rounded-full ${band ? BAND_BAR[band] : "bg-slate-400"}`} style={{ width: `${profile.percent}%` }} />
          </div>
          <ul className="mt-4 space-y-1.5">
            {profile.items.map((item) => (
              <li key={item.key} className="flex items-center gap-2 text-xs">
                {item.done ? (
                  <CheckIcon sx={{ fontSize: 14 }} className="text-emerald-500" />
                ) : (
                  <CloseIcon sx={{ fontSize: 14 }} className="text-rose-500" />
                )}
                <span className={item.done ? "text-slate-500 dark:text-slate-400" : "text-slate-700 dark:text-slate-200 font-medium"}>{item.label}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {data.slug && <Caption>Public page: /{data.slug}</Caption>}
    </div>
  );
}

export default function AnalyticsBookMyDentist() {
  const period = useAnalyticsPeriod();
  const navigate = useNavigate();
  const { data, loading } = useAnalyticsFetch(() => getBookMyDentist({ from: period.from, to: period.to }), [period.from, period.to]);
  const goToBooking = () => navigate("/client/clinical-settings/booktreatment");
  // BookMyDentist only clinics have no billing, so no money tiles
  const billingEnabled = isBillingEnabled();

  if (!loading && data && data.listed === false) {
    return (
      <div className="space-y-4 text-slate-800 dark:text-slate-200">
        <EmptyState title="This clinic is not listed on BookMyDentist yet">
          Create your public profile under{" "}
          <button type="button" className={LINK_BTN} onClick={goToBooking}>
            Settings → Booking
          </button>{" "}
          to start receiving online bookings and reviews.
        </EmptyState>
      </div>
    );
  }

  const listed = data && data.listed ? data : null;

  return (
    <div className="space-y-4 text-slate-800 dark:text-slate-200">
      <PeriodPicker period={period} />

      {loading || !listed ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <CardSkeleton cards={6} height="h-28" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <KpiCard label="Paid online" value={formatNumber(listed.paidOnline)} change={pctChange(listed.paidOnline, listed.funnelPrevious.paid)} />
            <KpiCard label="Pay at clinic" value={formatNumber(listed.payAtClinic)} change={pctChange(listed.payAtClinic, Math.max(0, listed.funnelPrevious.booked - listed.funnelPrevious.paid))} />
            <KpiCard
              label="Show-up rate"
              value={formatPct(listed.showUpRate)}
              change={pctChange(listed.showUpRate, listed.previousShowUpRate)}
              hint={`previous period ${formatPct(listed.previousShowUpRate)}`}
            />
            {billingEnabled && (
              <>
                <KpiCard label="Online revenue" value={formatRs(listed.onlineRevenue.collected)} hint="Collected from patients who came via BookMyDentist" />
                <KpiCard label="Online receipts" value={formatNumber(listed.onlineRevenue.receipts)} hint="Receipts in this period" />
                <KpiCard label="Online patients" value={formatNumber(listed.onlineRevenue.patients)} hint="Patients who paid in this period" />
              </>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ChartCard title="Booking funnel" hint="Profile views to completed visits in this period">
              <Funnel stages={listed.funnel} previous={{ ...listed.funnelPrevious }} />
            </ChartCard>
            <div className="lg:col-span-2">
              <ChartCard title="Online bookings" hint={listed.period.granularity === "month" ? "Per month · paid online vs pay at clinic" : "Per day · paid online vs pay at clinic"}>
                {seriesIsEmpty(listed.series, ["paidOnline", "payAtClinic"]) ? (
                  <p className="text-sm text-slate-400 dark:text-slate-500 py-10 text-center">No online bookings in this period</p>
                ) : (
                  <PeriodBarChart series={listed.series} specs={[{ key: "paidOnline", label: "Paid online" }, { key: "payAtClinic", label: "Pay at clinic" }]} stacked />
                )}
              </ChartCard>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ReviewsCard reviews={listed.reviews} />
            <ProfileCard data={listed} onEdit={goToBooking} />
          </div>
        </>
      )}
    </div>
  );
}
